import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import {
  MagnifyingGlass, ListChecks, Suitcase, AirplaneTakeoff, House, ArrowRight, Clock,
} from "@phosphor-icons/react";

const CHOICES = [
  { key: "exploring", icon: MagnifyingGlass, route: "/esplora", requiresAuth: false, tone: "sand" },
  { key: "deciding", icon: ListChecks, route: "/confronta", requiresAuth: false, tone: "sage" },
  { key: "organizing", icon: Suitcase, route: "/organizza", requiresAuth: false, tone: "terracotta" },
  { key: "aboard", icon: AirplaneTakeoff, route: "/abordo", requiresAuth: false, tone: "aboard" },
  { key: "returned", icon: House, route: "/dashboard", requiresAuth: false, tone: "ink", soon: true },
];

// Active tones ("sand", "sage", "terracotta", "aboard") share the same filled
// treatment — each with its own brand accent — to signal a live, clickable
// destination. "ink" stays muted and is reserved for phases that are still
// coming-soon (currently only "Sono rientrato"/Landing home).
const toneClasses = {
  sand: "bg-sand/25 border-sand/40",
  sage: "bg-sage/15 border-sage/40",
  terracotta: "bg-terracotta/15 border-terracotta/40",
  // "Sono già partito" — stays in the warm terracotta family (active), with a
  // slightly deeper fill/border to differentiate it from "Ho scelto, ora
  // organizzo" while keeping the same "filled, not grey" visual language.
  aboard: "bg-terracotta-light/60 border-terracotta/50",
  ink: "bg-ink/5 border-ink/15",
};

export default function Landing() {
  const { t, user, updateAnonState } = useApp();
  const navigate = useNavigate();
  const [soonOpen, setSoonOpen] = useState(false);

  // Fire Plausible custom event so the conversion funnel can start
  // consistently from "Landing Viewed" instead of a raw pageview.
  useEffect(() => {
    trackEvent("Landing Viewed");
  }, []);

  const pick = async (choice) => {
    await updateAnonState({ onboarding_choice: choice.key });
    if (choice.soon) {
      setSoonOpen(true);
      return;
    }
    if (choice.requiresAuth && !user) {
      navigate("/login", { state: { after: choice.route } });
    } else {
      navigate(choice.route);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="max-w-3xl mx-auto px-5 pt-10 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8"
        >
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-3 font-sans">
              {t.app.name}
            </p>
            <h1 data-testid="landing-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              {t.landing.title}
            </h1>
            <p className="mt-4 text-ink-soft text-base sm:text-lg max-w-xl leading-relaxed">
              {t.landing.subtitle}
            </p>
            <p
              data-testid="landing-intro"
              className="mt-4 text-sm text-ink-soft/75 max-w-xl leading-relaxed font-sans"
            >
              {t.landing.intro}
            </p>
            <p
              data-testid="landing-parent-note"
              className="mt-4 text-xs sm:text-sm text-ink-soft/70 max-w-xl leading-relaxed font-sans"
            >
              {t.landing.parentNote}
            </p>
          </div>
          <MascotPlaceholder emotion="happy" src="/assets/boardy-energico.png" className="shrink-0 self-start sm:self-end" />
        </motion.div>

        <div className="space-y-3">
          {CHOICES.map((c, idx) => {
            const Icon = c.icon;
            const label = t.landing.choices[c.key];
            return (
              <motion.button
                key={c.key}
                data-testid={`landing-choice-${c.key}`}
                onClick={() => pick(c)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + idx * 0.08 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className={`w-full text-left rounded-3xl border p-5 sm:p-6 flex items-center gap-4 transition-shadow shadow-[0_4px_20px_rgba(61,64,91,0.04)] hover:shadow-[0_10px_40px_rgba(61,64,91,0.08)] ${toneClasses[c.tone]}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0 border border-border">
                  <Icon size={28} weight="duotone" className="text-ink" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-lg sm:text-xl text-ink">
                      {label.title}
                    </h3>
                    {c.soon && (
                      <span className="text-[10px] uppercase tracking-widest bg-ink/10 text-ink-soft px-2 py-0.5 rounded-full font-sans font-semibold">
                        {t.common.soon}
                      </span>
                    )}
                  </div>
                  <p className="text-ink-soft text-sm sm:text-base mt-1 leading-relaxed">
                    {label.desc}
                  </p>
                </div>
                <ArrowRight size={22} weight="bold" className="text-ink-soft shrink-0" />
              </motion.button>
            );
          })}
        </div>

        <p className="mt-6 text-center text-xs text-ink-soft leading-relaxed max-w-md mx-auto">
          {t.landing.soonBanner}
        </p>
      </section>

      <Dialog open={soonOpen} onOpenChange={setSoonOpen}>
        <DialogContent data-testid="soon-phase-modal" className="max-w-md">
          <DialogHeader>
            <div className="h-12 w-12 rounded-2xl bg-terracotta/10 flex items-center justify-center mb-3">
              <Clock size={26} weight="duotone" className="text-terracotta" />
            </div>
            <DialogTitle className="font-display font-black text-2xl text-ink tracking-tight text-left">
              {t.common.soonPhaseTitle}
            </DialogTitle>
            <DialogDescription className="text-ink-soft font-sans leading-relaxed text-left pt-1">
              {t.common.soonPhaseBody}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="soon-phase-close"
              onClick={() => setSoonOpen(false)}
              className="w-full sm:w-auto rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold"
            >
              {t.common.soonPhaseCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
